import { Box, ButtonBase, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import GavelIcon from "@mui/icons-material/Gavel";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArticleIcon from "@mui/icons-material/Article";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";

const ICON_MAP = {
  gavel: GavelIcon,
  upload_file: UploadFileIcon,
  article: ArticleIcon,
  auto_fix_high: AutoFixHighIcon,
  attach_file: AttachFileIcon,
};

function SourceIcon({ iconKey, color }) {
  const Icon = ICON_MAP[iconKey] ?? AttachFileIcon;
  return <Icon sx={{ fontSize: 18, color }} />;
}

/**
 * Tarjeta de un archivo. Obligatoria (verde, candado, no se puede quitar) u
 * opcional (se marca/desmarca con un clic). Debajo del nombre va el tipo.
 */
function SourceCard({ src, fixed, selected, hint, onToggle }) {
  const active = fixed || selected;

  return (
    <ButtonBase
      role={fixed ? undefined : "checkbox"}
      aria-checked={fixed ? undefined : selected}
      aria-disabled={fixed || undefined}
      onClick={fixed ? undefined : () => onToggle(src.source_type)}
      disableRipple={fixed}
      sx={(theme) => {
        const tone = fixed ? theme.palette.success.main : theme.palette.primary.main;
        return {
          position: "relative",
          width: "100%",
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.2,
          px: 1.4,
          py: 1.1,
          textAlign: "left",
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: active ? alpha(tone, 0.55) : theme.palette.divider,
          bgcolor: active ? alpha(tone, theme.palette.mode === "dark" ? 0.14 : 0.07) : "transparent",
          cursor: fixed ? "default" : "pointer",
          transition: "border-color .15s, background-color .15s, box-shadow .15s, transform .15s",
          ...(!fixed && {
            "&:hover": {
              borderColor: alpha(tone, 0.8),
              boxShadow: `0 4px 14px ${alpha(tone, 0.14)}`,
              transform: "translateY(-1px)",
            },
          }),
          "&.Mui-focusVisible": { outline: `2px solid ${alpha(tone, 0.6)}`, outlineOffset: 2 },
        };
      }}
    >
      <Box
        sx={(theme) => {
          const tone = fixed ? theme.palette.success.main : theme.palette.primary.main;
          return {
            width: 34,
            height: 34,
            borderRadius: 2,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            bgcolor: active ? alpha(tone, 0.16) : theme.palette.action.hover,
          };
        }}
      >
        <SourceIcon
          iconKey={src.icon_key}
          color={fixed ? "success.main" : selected ? "primary.main" : "text.secondary"}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pr: 2.2 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: active ? 800 : 600,
            lineHeight: 1.25,
            color: active ? "text.primary" : "text.secondary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {src.label}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            mt: 0.3,
            fontWeight: 700,
            color: fixed ? "success.main" : "text.secondary",
            opacity: fixed ? 0.9 : 0.75,
          }}
        >
          {fixed ? "Obligatorio" : src.type_label ?? "Otros"}
          {hint && (
            <Box component="span" sx={{ color: "warning.main" }}>
              {` · ${hint}`}
            </Box>
          )}
        </Typography>
      </Box>

      <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex" }}>
        {fixed ? (
          <LockRoundedIcon sx={{ fontSize: 16, color: "success.main" }} />
        ) : selected ? (
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
        ) : (
          <AddCircleOutlineRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        )}
      </Box>
    </ButtonBase>
  );
}

const GRID_SX = {
  display: "grid",
  gap: 1,
  gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
};

const SECTION_TITLE_SX = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  mb: 0.8,
};

/**
 * Selector de archivos que acompañan al escrito (fuentes): arriba los
 * obligatorios del escrito y abajo, en una sola grilla, el resto ordenado por
 * tipo (Demanda, Archivos del abogado, …).
 *
 * - sourceDefinitions: fuentes activas del sistema.
 * - sourceConfigs: configuración del tipo de escrito (fijas / sugeridas).
 * - selected: source_type marcados.
 * - onToggle(sourceType)
 * - getHint(src, selected): texto opcional bajo el nombre (ej. "Código en col. 2").
 */
export default function SourcePicker({ sourceDefinitions = [], sourceConfigs = [], selected = [], onToggle, getHint }) {
  const isFixed = (src) => {
    const cfg = sourceConfigs.find((sc) => sc.source_type === src.source_type);
    return !!cfg && !cfg.is_user_selectable;
  };

  const byType = (a, b) =>
    (a.type_order ?? 99) - (b.type_order ?? 99) || (a.sort_order ?? 0) - (b.sort_order ?? 0);

  const fixedSources = sourceDefinitions.filter(isFixed).sort(byType);
  const optionalSources = sourceDefinitions.filter((src) => !isFixed(src)).sort(byType);
  const totalSelected = fixedSources.length + optionalSources.filter((src) => selected.includes(src.source_type)).length;

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.03) : alpha(theme.palette.primary.main, 0.025),
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.8 }}>
        <Box
          sx={(theme) => ({
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            flexShrink: 0,
          })}
        >
          <AttachFileRoundedIcon sx={{ fontSize: 20, color: "primary.main" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
            Archivos que acompañan al escrito
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.65 }}>
            Tocá un archivo para sumarlo o quitarlo. Los que tienen candado son obligatorios.
          </Typography>
        </Box>
        <Chip
          label={`${totalSelected} ${totalSelected === 1 ? "archivo" : "archivos"}`}
          size="small"
          color={totalSelected ? "primary" : "default"}
          sx={{ fontWeight: 800, flexShrink: 0 }}
        />
      </Box>

      {sourceDefinitions.length === 0 ? (
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          No hay fuentes de documentos configuradas.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
          {fixedSources.length > 0 && (
            <Box>
              <Typography sx={{ ...SECTION_TITLE_SX, color: "success.main" }}>
                Obligatorios de este escrito
              </Typography>
              <Box sx={GRID_SX}>
                {fixedSources.map((src) => (
                  <SourceCard key={src.source_type} src={src} fixed />
                ))}
              </Box>
            </Box>
          )}

          {optionalSources.length > 0 && (
            <Box>
              <Typography sx={{ ...SECTION_TITLE_SX, opacity: 0.6 }}>
                {fixedSources.length > 0 ? "Podés sumar" : "Elegí cuáles adjuntar"}
              </Typography>
              <Box sx={GRID_SX}>
                {optionalSources.map((src) => {
                  const isSelected = selected.includes(src.source_type);
                  return (
                    <SourceCard
                      key={src.source_type}
                      src={src}
                      selected={isSelected}
                      hint={getHint?.(src, isSelected)}
                      onToggle={onToggle}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
